import { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useForm } from "../../utils/useForm";
import { useNavigate } from "react-router-dom";
import { validateOnboarding } from "../../utils/login";
import TextInput from "./../../components/forms/TextInput";
import BallsBackground from "../../components/BallsBackground";
import DgButton from "../../components/DgButton";
import ProviderLogin from "./ProviderLogin";
import TwoFactorVerification from "./TwoFactorVerification";
import Request from "../../utils/fetchUtils";
import useFragment from "../../utils/useFragment";
import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/register.css";

/**
 * @file Login.jsx
 * @description Handles user authentication and session initialization
 *
 * @section Responsibilities
 * - Manage user authentication flow
 * - Handle form validation
 * - Initialize user session
 * - Redirect based on user role
 * - Handle authentication errors
 * - Manage loading states
 *
 * @section Dependencies
 * - sessionUtils: Session management and validation
 * - ROLES: Role definitions and permissions
 * - FormValidation: Input validation and sanitization
 * - React Router: Navigation and routing
 *
 * @section Integration Points
 * - Authentication API: User verification and token generation
 * - Session Management: Session initialization and persistence
 * - Navigation System: Post-login routing and redirection
 * - Role Management: User role assignment and validation
 *
 * @section State Management
 * - formData: User credentials and form state
 * - loading: Authentication process status
 * - error: Error messages and validation states
 * - rememberMe: Session persistence preference
 *
 * @section Events
 * - onSubmit: Form submission and authentication
 * - onError: Error handling and user feedback
 * - onSuccess: Successful login and redirection
 * - onInputChange: Form field updates
 *
 * @section Props
 * @prop {Function} setUserRole - Updates user role in parent component
 */

const Login = ({ setUserRole }) => {
  const fragmentData = useFragment();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState(null);

  const [data, setData] = useForm({
    email: "",
    password: "",
  });

  const { email, password } = data;

  const isActive = (path) => {
    if (window.location.pathname == path) {
      return "active";
    }
    return "";
  };

  const processThirdPartyLogin = async (access_token, id_token) => {
    setLoading(true);
    const res = await Request(
      "/user/auth?isTrirdParty=true",
      {
        access_token,
        id_token,
      },
      "POST"
    );
    setLoading(false);
    if (res.ok) {
      const { userData, token, accessToken } = res.data;
      setUserRole(userData.role);
      localStorage.setItem("jwt", token);
      localStorage.setItem("accessToken", accessToken);
      localStorage.session = JSON.stringify(userData);
      validateOnboarding(userData, navigate);
    }
  };
  const processLogin = async (data) => {
    setLoading(true);
    setError("");
    
    const res = await Request(
      "/user/auth",
      {
        ...data,
        username: data.email.toLowerCase(),
      },
      "POST"
    );
    setLoading(false);
    
    if (res.ok) {
      // Check if 2FA is required
      if (res.data.requires2FA) {
        setLoginCredentials(data);
        setShowTwoFactor(true);
        return;
      }
      
      // Normal login flow
      const { userData, token, accessToken } = res.data;
      setUserRole(userData.role);
      localStorage.setItem("jwt", token);
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("session", JSON.stringify(userData));
      Object.keys(userData).forEach((k) => {
        const value = userData[k];
        const parsedValue =
          typeof value === "object" ? JSON.stringify(value) : value;
        localStorage.setItem(k, parsedValue);
      });
      validateOnboarding(userData, navigate);
    } else {
      setError(res.message);
    }
  };
  useEffect(() => {
    const { access_token, id_token } = fragmentData;
    if (access_token && id_token) {
      processThirdPartyLogin(access_token, id_token);
    }  }, [fragmentData, processThirdPartyLogin]);

  // Show 2FA verification if required
  if (showTwoFactor && loginCredentials) {
    return (
      <div className="auth-background">
        <TwoFactorVerification
          credentials={loginCredentials}
          setUserRole={setUserRole}
          onSuccess={() => {
            setShowTwoFactor(false);
            setLoginCredentials(null);
          }}
          onCancel={() => {
            setShowTwoFactor(false);
            setLoginCredentials(null);
            setError("");
          }}
        />
      </div>
    );
  }

  return (
    <BallsBackground>
      <div className="reduced-content reduced-container shadow1">
        <div className="inner-content">
          <div className="auth-select">
            <div
              className={`auth-option ${isActive("/login")} ${isActive("/")}`}
            >
              <span>Log In</span>
            </div>
            <div
              onClick={() => navigate("/register")}
              className={`auth-option ${isActive("/register")}`}
            >
              <span>Register</span>
            </div>
          </div>
          <div className="content-wrapper">
            <div className="heading">
              <h1 className="title">Log in</h1>
            </div>
            <Grid
              container
              rowSpacing={4}
              columnSpacing={{ xs: 0, sm: 2, md: 12 }}
            >
              <Grid item xs={12}>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      type: "email",
                      label: "Email",
                      name: "email",
                      value: email,
                      action: setData,
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      type: "password",
                      label: "Password",
                      name: "password",
                      value: password,
                      action: setData,
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
              </Grid>
            </Grid>
            <div className="button-container">
              {error && <div className="error">{error}</div>}
              <DgButton
                category={"primary"}
                size={"large"}
                text={"Log in"}
                action={() => processLogin(data)}
                loading={loading}
              />
            </div>
            <br />
            <div
              className="sign-in-with"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/recover-password")}
            >
              Forgot your password?
            </div>
            <div className="blocked">
              <div className="separator"></div>
              <div>
                <div className="sign-in-with">or sign in with:</div>
                <div className="providers-logos">
                  <ProviderLogin provider="Google" />
                  <ProviderLogin provider="Facebook" />
                  <ProviderLogin provider="SignInWithApple" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BallsBackground>
  );
};

export default Login;
